/**
 * Your Next Move™ + Desk Setup step wording — the ONE source for every surface:
 * the ribbon and Your Desk card (via src/lib/deskNextMove.ts / deskHabit.ts), the
 * morning brief and Ask Cannafax (edge functions import this file directly).
 * Rules, order, cooldowns and routes are data (desk_next_move_rules); detection is
 * desk_next_move_signal(); choice is get_desk_next_move(). This file only says it.
 * Copy: Your Next Move™ spec, move table (customer wording uses "listing", never "lot").
 */

/** Your Next Move™ persona (desk_company_persona): power traders get buyer and seller moves together. */
export type DeskPersona = "seller" | "buyer" | "power_trader";

/** The side a move speaks to — a power trader's move keeps the side of the rule it came from. */
export function moveSide(m: { persona: DeskPersona; side?: "seller" | "buyer" }): "seller" | "buyer" {
  return m.side ?? (m.persona === "buyer" ? "buyer" : "seller");
}

export type DeskMove =
  | { kind: "move"; persona: DeskPersona; side?: "seller" | "buyer"; key: string; priority: number; vars: Record<string, unknown>; route: string; cue: string; version?: number;
      /** Admin wording override (desk_next_move_rules.line_template/button_label); blank = default wording below. */
      copy?: { line?: string; button?: string } }
  | { kind: "setup"; persona: DeskPersona; key: string; label: string; count: number; target: number; route: string; cue: string | null }
  | { kind: "caught_up"; persona: DeskPersona };

export const CAUGHT_UP_LINE = "You're all caught up.";
export const NEXT_MOVE_LABEL = "Your Next Move™";
export const NOT_NOW = "Done";

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);
const s = (v: unknown, fallback: string) => (typeof v === "string" && v.trim() ? v : fallback);
const num = (v: unknown) => (typeof v === "number" ? v : Number(v) || 0);

/** Desk Setup step name for the person's persona — brokers/power traders hear supply and demand wording. */
export function setupLabel(key: string, label: string, persona?: DeskPersona): string {
  if (persona === "power_trader") {
    if (key === "lots") return "Add Available Supply";
    if (key === "buyer_request") return "Post a Buy Request";
  }
  return label;
}

/** Desk Setup step line ("Time to Magic" setup, co-founder proposal v3). */
export function setupLine(key: string, count: number, target: number, label: string, persona?: DeskPersona): string {
  const pt = persona === "power_trader";
  switch (key) {
    case "logo": return "Put your name and logo on every menu so buyers trust who it's coming from.";
    case "lots":
      if (pt) return "Upload your available inventory. Source and grower identities are 100% confidential and never exposed.";
      return count === 0 ? "Drop your live inventory with specs and wholesale pricing—or forward your sheet to ingest@cannafax.ai." : `${Math.min(count, target)} of ${target} listings live. Buyers like a real choice.`;
    case "menus": return count === 0 ? "Turn your live inventory into a private, protected price sheet ready for buyers." : "Set up another VIP menu for a different group of buyers.";
    case "shares": return pt ? "Send your confidential wholesale menu directly to buyers via text, WhatsApp, or email link." : "Send your live, private menu link to buyers via text, WhatsApp, or email so they can place orders.";
    case "invites": return count === 0 ? "Put your menu in your buyers' inboxes. We'll notify you the minute they open it." : "Invite another buyer to your menu.";
    case "buyer_request": return pt ? "Broadcast what your buyers are looking for to match against inbound grower supply and Bridge Matches." : "Tell us what you're looking for with target specs and quantity so verified suppliers can quote you.";
    case "buyer_send": return "Invite the suppliers you already buy from so they see your requests and you see their live private drops.";
    case "buyer_logo": return "Put your company name on your requests so suppliers know who's asking.";
    case "buyer_team": return "Bring your purchasing team in to review deals with you.";
    case "supplier_menus": return count === 0 ? "Open a supplier's menu to see what's available." : "Check another supplier's menu.";
    case "hunting": return "Save a search and we'll watch every menu for it.";
    case "alerts": return "Get a short email when menus you follow change.";
    case "buyer_messages": return "See something you like? Ask the supplier about it.";
    default: return label;
  }
}

/** Desk Setup step button — always names the action. */
export function setupButton(key: string, persona?: DeskPersona): string | null {
  const pt = persona === "power_trader";
  const labels: Record<string, string> = {
    logo: "Set Brand", lots: pt ? "Add Supply" : "Add Inventory", menus: "Create Menu", shares: "Share Menu", invites: "Send Invites",
    buyer_request: pt ? "Post Demand" : "Post Request", buyer_send: "Invite Suppliers", buyer_logo: "Set Brand", buyer_team: "Invite Teammate",
    supplier_menus: "See supplier menus", hunting: "Save a search", alerts: "Turn on alerts", buyer_messages: "Message a supplier",
  };
  return labels[key] ?? null;
}

/**
 * Personal commitment cards. Per-user only (Option A): read for the signed-in person, never via the
 * company signal, so teammates and company emails never see them. Parked option: admin- or
 * user-driven "Share with team" on chosen notes (see memory: features/ai/memory-privacy-boundary).
 */
export const isCommitmentMove = (key: string) => key === "seller_commitment_due" || key === "buyer_commitment_due";

/** Line + button for one move. Unknown rule → null (never show a raw key). */
export function moveCopy(key: string, v: Record<string, unknown>): { line: string; button: string } | null {
  const n = num(v.n);
  if (isCommitmentMove(key)) {
    const who = typeof v.company === "string" && v.company.trim() ? v.company : null;
    const late = num(v.days_overdue);
    const when = late <= 0 ? "today" : late === 1 ? "yesterday" : `${late} days ago`;
    return {
      line: who ? `You promised ${who} a follow-up ${late <= 0 ? "for today" : `— due ${when}`}.` : `A follow-up you promised was due ${when}.`,
      button: who && typeof v.company_id === "string" ? "Message them" : "",
    };
  }
  switch (key) {
    case "seller_reply_waiting": return { line: `${s(v.company, "A buyer")} is waiting on your reply.`, button: "Reply" };
    case "seller_listing_watched": return { line: `A buyer is watching ${s(v.listing, "one of your listings")}. Keep its price and quantity current.`, button: "Review listing" };
    case "seller_buyers_looking": return { line: `${n} ${plural(n, "buyer company", "buyer companies")} looked at your menus today. A quick hello lands best now.`, button: "See who's looking" };
    case "seller_unpriced": return { line: `${n} active ${plural(n, "listing has", "listings have")} no price. Priced listings get more inquiries.`, button: "Add prices" };
    case "seller_stale": return { line: `${n} ${plural(n, "listing hasn't", "listings haven't")} been updated in two weeks. Buyers trust fresh numbers.`, button: "Review listings" };
    case "seller_first_reads": return { line: "Your invites are out. While buyers check their inbox, text your menu link to your top two buyers.", button: "Text your menu" };
    case "seller_menu_quiet": return {
      line: v.days == null ? "Your menus haven't gone out yet. A short update brings buyers back." : `Your menus haven't gone out in ${num(v.days)} days. A short update brings buyers back.`,
      button: "Send an update",
    };
    case "buyer_reply_unread": return { line: `${s(v.supplier, "A supplier")} replied to you.`, button: "Read reply" };
    case "buyer_watched_changed": return { line: `${s(v.listing, "A listing you watch")} just changed ${v.change === "quantity" ? "quantity" : "price"}.`, button: "See the listing" };
    case "buyer_search_matches": return { line: `${n} new ${plural(n, "listing matches", "listings match")} "${s(v.search, "your search")}".`, button: "See matches" };
    case "buyer_menu_updated": return { line: `${s(v.supplier, "A supplier")} updated their menu.`, button: "Open menu" };
    case "buyer_no_search": return { line: "Save a search and we'll watch every menu for it.", button: "Save a search" };
    case "seller_request_fillable": return { line: `${s(v.company, "A buyer you work with")} is looking for something you have: ${s(v.listing, "one of your listings")}.`, button: `Message ${s(v.company, "them")}` };
    case "seller_bridge_match": return {
      line: num(v.spread_per_lb) > 0
        ? `${s(v.company, "A buyer")} wants ${s(v.listing, "a listing")}. You can source it with about $${num(v.spread_per_lb).toLocaleString()}/lb to spare.`
        : `${s(v.company, "A buyer")} wants ${s(v.listing, "a listing")} you can source.`,
      button: `Message ${s(v.company, "the buyer")}`,
    };
    case "seller_milestone_watchers": return { line: `${num(v.watchers)} buyers are watching ${s(v.listing, "one of your listings")}. Nice work.`, button: "See the listing" };
    case "seller_market_price": return { line: `${s(v.listing, "One of your listings")} is priced ${num(v.pct)}% ${v.dir === "below" ? "below" : "above"} similar listings.`, button: "Review price" };
    case "buyer_request_matched": return { line: `${n} new ${plural(n, "listing fits", "listings fit")} your Buy Request.`, button: "See matches" };
    case "buyer_request_unanswered": {
      const saved = num(v.saved_suppliers);
      const ask = saved > 0 ? `Send it to your ${saved} saved ${plural(saved, "supplier", "suppliers")}.` : "Send it to the suppliers you usually buy from.";
      return {
        line: v.want ? `Your request for "${s(v.want, "")}" hasn't reached the right supplier yet. ${ask}` : `Your Buy Request hasn't reached the right supplier yet. ${ask}`,
        button: saved > 0 ? `Send to ${saved} ${plural(saved, "supplier", "suppliers")}` : "Invite suppliers",
      };
    }
    case "buyer_hunt_new_listings": return { line: `${n} fresh ${plural(n, "listing", "listings")} landed on menus you follow.`, button: "Take a look" };
    default: return null;
  }
}

const ago = (hours: number) =>
  hours < 24 ? `${hours} ${plural(hours, "hour", "hours")}` : `${Math.floor(hours / 24)} ${plural(Math.floor(hours / 24), "day", "days")}`;

/** The quiet "why" under a suggestion — facts only, from the signal's own numbers. Null = no evidence line. */
export function moveEvidence(key: string, v: Record<string, unknown>): string | null {
  const n = num(v.n);
  if (isCommitmentMove(key)) return typeof v.note === "string" ? `Your note: “${v.note}”` : null;
  switch (key) {
    case "seller_reply_waiting": return v.hours ? `Their message has been waiting ${ago(num(v.hours))}.` : null;
    case "seller_listing_watched": {
      const names = buyerNames(v);
      if (names.length === 1 && num(v.watchers) <= 1) return `${names[0]} added it to their watchlist${v.hours ? ` ${ago(num(v.hours))} ago` : ""}.`;
      return num(v.watchers) > 1 ? `${num(v.watchers)} buyers are watching it.` : "A buyer added it to their watchlist in the last two days.";
    }
    case "seller_buyers_looking": return "Menu visits and listings opened in the last 24 hours.";
    case "seller_unpriced": return "Active listings with no price set.";
    case "seller_stale": return v.days ? `The oldest hasn't been touched in ${num(v.days)} days.` : null;
    case "seller_menu_quiet": return v.days == null ? "No menu update has ever gone out." : "Counted from your last menu update.";
    case "seller_first_reads": return "Your invites are out; no buyer has opened a menu yet.";
    case "buyer_reply_unread": return v.hours ? `Replied ${ago(num(v.hours))} ago.` : null;
    case "buyer_watched_changed": return "Changed since you started watching it.";
    case "buyer_search_matches": return "New since you saved the search.";
    case "buyer_menu_updated": return "New or changed listings since your last visit.";
    case "seller_request_fillable": return typeof v.want === "string" ? `Their Buy Request: “${v.want}”` : "From their Buy Request.";
    case "seller_bridge_match": return num(v.buyer_per_lb) && num(v.supplier_per_lb)
      ? `Their price: $${num(v.buyer_per_lb).toLocaleString()}/lb · ${s(v.supplier, "Supplier")} menu: $${num(v.supplier_per_lb).toLocaleString()}/lb. Only your team sees both.`
      : "Only your team sees both sides of this match.";
    case "seller_milestone_watchers": return "Buyer companies watching it right now.";
    case "seller_market_price": return `Compared with the typical price of similar ${s(v.form, "")} listings across many suppliers. No single company's price is used.`.replace("  ", " ");
    case "buyer_request_matched": return "New in the last three days.";
    case "buyer_request_unanswered": return v.hours != null ? `Posted ${ago(num(v.hours))} ago. No supplier on Cannafax has a fit yet.` : "No supplier on Cannafax has a fit yet.";
    case "buyer_hunt_new_listings": return "Added in the last three days.";
    default: return null;
  }
}

const buyerNames = (v: Record<string, unknown>): string[] =>
  Array.isArray(v.buyers) ? v.buyers.filter((b): b is string => typeof b === "string" && !!b.trim()).slice(0, 5) : [];

/**
 * Plain facts behind a suggestion, for Ask Cannafax to answer from directly
 * ("which buyer?" → the company). Company names only — never people, never
 * grower/source identity.
 */
export function moveFacts(m: DeskMove): string[] {
  if (m.kind !== "move") return [];
  const v = m.vars ?? {};
  const out: string[] = [];
  if (typeof v.note === "string") out.push(`What the user promised (their own note): ${v.note}`);
  if (typeof v.due_on === "string") out.push(`Due on: ${v.due_on}`);
  if (typeof v.listing === "string") out.push(`Listing: ${v.listing}`);
  if (typeof v.company === "string") out.push(`Buyer company: ${v.company}`);
  if (typeof v.supplier === "string") out.push(`Supplier: ${v.supplier}`);
  const names = buyerNames(v);
  if (names.length) out.push(`Buyer companies watching (most recent first): ${names.join(", ")}`);
  if (v.watchers != null) out.push(`Total buyers watching: ${num(v.watchers)}`);
  if (v.hours != null) out.push(`How long ago: ${ago(num(v.hours))}`);
  if (v.n != null) out.push(`Count: ${num(v.n)}`);
  if (v.days != null) out.push(`Days: ${num(v.days)}`);
  if (typeof v.search === "string") out.push(`Saved search: ${v.search}`);
  if (typeof v.change === "string") out.push(`What changed: ${v.change}`);
  const to = typeof v.to === "string" && v.to.startsWith("/") ? v.to : m.route;
  out.push(`Where to act: ${to}`);
  return out;
}

/** Starter questions shown under the pinned move in Ask Cannafax. */
export function moveStarters(m: DeskMove): string[] {
  if (m.kind === "caught_up") return [];
  const first = m.kind === "move" && m.key === "seller_listing_watched" ? "Who is watching it?" : "Why this?";
  return [first, "What else could I do?", "Help me do it"];
}

/** The question an "Ask about this" button seeds into Ask Cannafax — carries the line and its facts, never a raw rule key. */
export function askAboutPrompt(m: DeskMove): string | null {
  if (m.kind === "caught_up") return null;
  const copy = deskMoveCopy(m);
  const what = m.kind === "setup" ? "Desk Setup step" : `${NEXT_MOVE_LABEL} suggestion`;
  const facts = copy.evidence ? ` (${copy.evidence.replace(/\.$/, "")})` : "";
  return `My ${what} says: "${copy.line}"${facts} What should I know before I act on it?`;
}

/** What every surface shows for the lookup result. Button null = no button (caught up). */
export function deskMoveCopy(m: DeskMove | null): { line: string; button: string | null; evidence: string | null } {
  if (!m || m.kind === "caught_up") return { line: CAUGHT_UP_LINE, button: null, evidence: null };
  if (m.kind === "setup") return { line: setupLine(m.key, m.count, m.target, m.label, m.persona as DeskPersona), button: setupButton(m.key, m.persona as DeskPersona), evidence: null };
  const mc = moveCopy(m.key, m.vars ?? {});
  if (!mc) return { line: CAUGHT_UP_LINE, button: null, evidence: null };
  const line = fillMoveTemplate(m.copy?.line, m.vars ?? {}) ?? mc.line;
  const button = mc.button ? (fillMoveTemplate(m.copy?.button, m.vars ?? {}) ?? mc.button) : mc.button;
  return { line, button, evidence: moveEvidence(m.key, m.vars ?? {}) };
}

/** Placeholders an admin may use in a move headline or button (kept in step with desk_next_move_validate_edit). */
export const MOVE_PLACEHOLDERS = ["company", "supplier", "listing", "search", "n", "days", "hours"] as const;

/**
 * Fill an admin wording override with the move's live facts. Returns null (→ default wording)
 * when there is no override, or when any placeholder has no value for this move — a half-filled
 * sentence is worse than the default one.
 */
export function fillMoveTemplate(tpl: string | null | undefined, v: Record<string, unknown>): string | null {
  if (!tpl || !tpl.trim()) return null;
  let missing = false;
  const out = tpl.replace(/\{([^}]*)\}/g, (_, k: string) => {
    const val = v[k];
    if (!(MOVE_PLACEHOLDERS as readonly string[]).includes(k) || val == null || (typeof val === "string" && !val.trim())) { missing = true; return ""; }
    return String(val);
  });
  return missing ? null : out;
}
